import { createLogger } from "@/debug/DebugingLogger";
import { checkSessionExpired, fetchWithSessionRetry } from "./utils";
const log = createLogger("Worker:MainTasks:Utils");

/** Simulate progress with a decaying interval (for uploads without real progress). */
function displayProgress(onProgress: (progress: number) => void): () => void {
  onProgress(0);
  let currentProgress = 0;
  const progressInterval = setInterval(() => {
    if (currentProgress < 90) {
      const increment = (90 - currentProgress) * 0.1;
      currentProgress = Math.min(90, currentProgress + increment);
      onProgress(Math.round(currentProgress));
    }
  }, 200);
  return () => clearInterval(progressInterval);
}

async function handleUpload(
  formData: FormData,
  baseUrl: string | URL,
  uploadType: string,
  fileName: string,
  fileSize: number,
  onProgress?: (progress: number) => void,
): Promise<Response> {
  log.debug(
    `Starting ${uploadType} upload for: ${fileName} (${(fileSize / (1024 * 1024)).toFixed(2)} MB)`,
  );
  const stopProgress = onProgress ? displayProgress(onProgress) : null;

  try {
    const response = await fetchWithSessionRetry(baseUrl, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    stopProgress?.();
    onProgress?.(90);

    if (!response.ok) {
      checkSessionExpired(response);
      const errorText = await response.text().catch(() => "");
      log.error(
        `${uploadType} upload failed. Status: ${response.status}`,
        errorText,
      );
      throw new Error(
        `${uploadType} upload failed with status ${response.status}: ${response.statusText}`,
      );
    }

    return response;
  } catch (error) {
    stopProgress?.();

    if (error instanceof Error) {
      log.error(`${uploadType} upload failed: ${error.message}`);
      throw error;
    }
    log.error(`${uploadType} upload failed: network error`);
    throw new Error(`${uploadType} upload failed: network error`);
  }
}

export async function uploadGoogleSheet(
  blob: Blob,
  sheetName: string,
  spreadsheetName: string,
  modelId: string,
  sheetId: string,
  range: string = "",
  baseUrl: string | URL,
  savedConnectionId: string,
  onProgress?: (progress: number) => void,
): Promise<Response> {
  const formData = new FormData();
  formData.append("model_id", modelId);
  formData.append("sheet_id", sheetId);
  formData.append("sheet_name", sheetName);
  formData.append("spreadsheet_name", spreadsheetName);
  formData.append("range", range);
  formData.append("saved_connection_id", savedConnectionId);
  formData.append("file", blob, sheetName);

  return handleUpload(
    formData,
    baseUrl,
    "Google Sheets",
    sheetName,
    blob.size,
    onProgress,
  );
}
